/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 * Created At: 05/12/2025
 */
import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {STATUS_MODEL} from "../../../../enums/statusEnum";
import {
    BackslashSvg,
    CheckCardIcon,
    CircleFilledChip,
    IconChipDot,
    IconCircleMessage,
    IconLine
} from "../../../../components/buttons/IconButtons";
import {getPhotoPicker, PHOTO_PICKER_MODEL} from "../../../../components/PhotoPicker/SubComponent/Models/Models";
import {REQUEST_APPROVAL_MODEL} from "../Model/Model";
import {toast} from "react-toastify";
import {
    CreateDocumentationApprovalUserLevel,
    CreateDocumentationApprovalVehicleLevel,
    GetDocumentationApprovalFlowByOptions,
    GetDocumentationApprovalFlowByVehicleOptions,
    GetDocumentationUserFlowByUserId,
    GetDocumentationVehicleFlowByUserId,
    GetUploadDocumentApprovalPeriod
}
    from "../../../../reduxToolkit/DocumentationApproval/DocumentationApprovalApi";
import {calculateHeight, errorMsg} from "../../../../Apis/functions";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {PROFILE_MODEL} from "../../../DocumentationApprovalFlow/Model/Model";
import {FIELD_TYPE} from "../../../Dashboard/Visitor/Type/Model/Model";

/**
 * requestTab app
 *
 * Custom hook/controller that manages the logic for the request tab
 * in the documentation approval flow it hanldes
 *
 * -fetching active approval periods
 * -fetching documentation approval periods
 * -managing draft and submit actions
 * -handling file and text inputs per level
 * -computing status icons per approval level
 * -temporary UI highlighting for selected levels
 *
 * Custom
 * @param userTypeId
 * @param modified - flag indicate external data modifications
 * @param setModified - setter to reset modifications state
 * @param profile - profile identifier (user/vehicle)
 * @param userId - User or entity identify
 * @param setPeriod -set period active
 * @param setDocumentationApproval
 * @returns {any} Apy with state and handlers for the request tab UI
 * @constructor
 */
export const RequestTabApp = ({
    modified,
    setModified,
    profile,
    userId,
    setPeriod,
    setDocumentationApproval
}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {user} = useSelector((state) => state.authenticatioauthennSlice);
    const userType = user?.data?.userType?.id;
    const [photoPicker, setPhotoPicker] = useState({...PHOTO_PICKER_MODEL});
    const [item, setItem] = useState({...REQUEST_APPROVAL_MODEL});
    const [emptyFields, setEmptyFields] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const elementRef = useRef(null);
    const availableHeightColumnOneRef = useRef(null);
    const availableHeightColumnTwoRef = useRef(null);
    const containerHeightRef = useRef(null);
    const levelRefsRef = useRef([]);
    const [activeLevelIndex, setActiveLevelIndex] = useState(null);
    const [filesByLevel, setFilesByLevel] = useState([]);
    const [highlightedLevel, setHighlightedLevel] = useState(false);

    useEffect(() => {
        calculateHeight({elementRef: availableHeightColumnOneRef, bottom: 30});
        calculateHeight({elementRef: availableHeightColumnTwoRef, bottom: 30});
        calculateHeight({elementRef: containerHeightRef, bottom: 60});
    },[item])

    /**
     *
     * handlers temporary highlight of a selected level
     * the highlight is automatically removed after 3 seconds
     *
     *
     * @param index - index of the selected
     */
    const handleSelectLevel = (index) => {
        setHighlightedLevel(index);

        setTimeout(() => {
            setHighlightedLevel(false);
        }, 3000);
    };

    /**
     * Calculates and sets css custom property (--top-value)
     * based on the vertical position of the main element
     * used to align ui indicators
     */
    useLayoutEffect(() => {
        if (elementRef?.current) {
            const rect = elementRef.current.getBoundingClientRect();
            const distanceTop = rect.top + 90;
            elementRef.current.style.setProperty('--top-value', `${distanceTop}px`);
        }
    }, []);


    /**
     * Effect: Load documentation flow on component mount.
     */
    useEffect(() => {
        if(userType){
            dispatch(GetUploadDocumentApprovalPeriod(userType))//Get period
                .then(({payload: {data: {data, success}}}) => {
                    if (success && data !== null) {
                        setPeriod(data);
                        const currentDate = new Date().getTime();
                        const leadDaysStart = new Date(data?.startDate) - data?.leadDays * 24 * 60 * 60 * 1000;
                        if(currentDate >= leadDaysStart && currentDate < data?.startDate){
                            getDataActiveDocumentApproval();
                        }
                    }
                });
        }
    }, [userType]);

    /**
     * Reloads data when the modified flag is set form outisde
     */
    useEffect(() => {
        if (modified) {
            getDataActiveDocumentApproval();
            setModified(false);
        }
    }, [modified]);

    /**
     * Fetches:
     * Activr documentation approval flow based on profile and user id
     *
     * updates internal state accordingly
     */
    const getDataActiveDocumentApproval = () => {
        const endpoint = (profile === PROFILE_MODEL.VEHICLE ? GetDocumentationApprovalFlowByVehicleOptions : GetDocumentationApprovalFlowByOptions)
        const params = {profile, id : userId}
        dispatch(endpoint(params))
            .then(({ payload: { data: { data, success, code } } }) => {
                if (success && data !== null) {
                    const flow = REQUEST_APPROVAL_MODEL.set(data);
                    setItem(flow);
                    getDocumentationUserFlow();
                } else if (!success) {
                    errorMsg(code);
                }
            });
    }

    /**
     * Fetches the documentation approval flow/status for the current user.
     * Updates the local documentationApproval state.
     */
    const getDocumentationUserFlow = () => {
        const endpoint = (profile === PROFILE_MODEL.VEHICLE ? GetDocumentationVehicleFlowByUserId : GetDocumentationUserFlowByUserId);
        dispatch(endpoint({id:userId ,userTypeId: userType})).then(({ payload: { data: { data, success, code } } }) => {//User documentation
            if (success) {
                if (data === null)
                    setDocumentationApproval(data?.approvalStatusId ?? STATUS_MODEL.PENDING);
                else
                    setDocumentationApproval(data?.approvalStatusId);
            } else
                errorMsg(code);
        });
    }


    /**
     * Returns the appropriate icon and connector line
     * for a documentation approval level based on its status.
     *
     * @param {number} statusId - Current status identifier
     * @param {number} index - Index of the level
     * @param {Array} levels - Full list of approval levels
     *
     * @returns {JSX.Element} Status icon representation
     */
    const getStatusIcon = (statusId, index, levels) => {
        const FINAL_STATUSES = [
            STATUS_MODEL.PENDING,
            STATUS_MODEL.DRAFT,
            STATUS_MODEL.PENDING_REVIEW,
            STATUS_MODEL.APPROVED,
            STATUS_MODEL.CHANGES_REQUESTED,
            STATUS_MODEL.DENIED
        ];

        const isLastLevel = index === levels.length -1;
        const renderLine = (className) => !isLastLevel && <div><IconLine className={className} /></div>;

        if (FINAL_STATUSES.includes(statusId)) {
            switch (statusId) {
                case STATUS_MODEL.DRAFT:
                    return (
                        <>
                            <div><CheckCardIcon className="w-16px h-16px icon-color-secondary-500"/></div>
                            {renderLine("icon-color-secondary-200")}
                        </>
                    );
                case STATUS_MODEL.PENDING_REVIEW:
                    return (
                        <>
                            <div><IconChipDot className="w-16px h-16px icon-color-shades-400"/></div>
                            {renderLine("icon-color-shades-200")}
                        </>
                    );
                case STATUS_MODEL.APPROVED:
                    return (
                        <>
                            <div><CheckCardIcon className="w-16px h-16px icon-color-success-500"/></div>
                            {renderLine("icon-color-success-200")}
                        </>
                    );
                case STATUS_MODEL.CHANGES_REQUESTED:
                    return (
                        <>
                            <div><IconCircleMessage className="w-16px h-16px icon-color-warning-500"/></div>
                            {renderLine("icon-color-warning-200")}
                        </>
                    );
                case STATUS_MODEL.DENIED:
                    return (
                        <>
                            <div><BackslashSvg className="w-16px h-16px icon-color-error-code-500"/></div>
                            {renderLine("icon-color-error-code-200")}
                        </>
                    );
            }
        }

        const isAfterFinalStatus = index > 0 && FINAL_STATUSES.includes(levels[index - 1]?.userLevel?.statusId);

        const hasPriorInteraction = levels.slice(0, index).some((lvl, i) => {
            const currentStatus = lvl?.userLevel?.statusId;
            if (FINAL_STATUSES.includes(currentStatus)) return true;
            return (
                i > 0 &&
                FINAL_STATUSES.includes(levels[i - 1]?.userLevel?.statusId)
            );
        });

        if (isAfterFinalStatus) {
            return (
                <>
                    <div><IconChipDot className="w-16px h-16px icon-color-shades-400"/></div>
                    {renderLine("icon-color-shades-200")}
                </>
            );
        } else if (!hasPriorInteraction) {
            return (
                <>
                    <div><CircleFilledChip className="w-16px h-16px icon-color-shades-200"/></div>
                    {renderLine("icon-color-shades-200")}
                </>
            );
        } else {
            return (
                <>
                    <div><CircleFilledChip className="w-16px h-16px icon-color-shades-200"/></div>
                    {renderLine("icon-color-shades-200")}
                </>
            );
        }
    };

    /**
     * Wathcher for changes in the photo picker and assings
     * the selected file to the currently active level
     */
    useEffect(() => {
        if (activeLevelIndex === null) return;

        const file = getPhotoPicker(photoPicker);
        if (!file) return;
        setFilesByLevel(prev => {
            const copy = [...prev];
            copy[activeLevelIndex] = file;
            return copy;
        });
        handleChangeFile(file, activeLevelIndex);
    }, [photoPicker]);
    const endpoint = (profile === PROFILE_MODEL.VEHICLE ? CreateDocumentationApprovalVehicleLevel : CreateDocumentationApprovalUserLevel)

    const appendCommonDraftFields = (formData) => {
        formData.append('userTypeId', userType);
        formData.append('statusId', STATUS_MODEL.DRAFT);
    };

    const appendCommonSubmitFields = (formData) => {
        formData.append('userTypeId', userType);
        formData.append('statusId', STATUS_MODEL.PENDING_REVIEW);
    };

    /**
     * Draft – VEHICLE
     */
    const buildVehicleDraftFormData = (levels) => {
        const formData = new FormData();
        appendCommonDraftFields(formData);
        formData.append('vehicleId', userId);

        levels.forEach((level, index) => {
            formData.append(`vehicles[${index}].documentationApprovalLevelId`, level.id);

            if (level.type === FIELD_TYPE.TEXT.name) {
                formData.append(`vehicles[${index}].document`, level.userLevel?.document || "");
            }
            else {
                const file = level.userLevel?.fileUpload;
                if (file) {
                    formData.append(`vehicles[${index}].fileUploadDto.files`, file);
                    formData.append(`vehicles[${index}].fileUploadDto.userFileNames`, file.name);
                }
            }
        });

        return formData;
    };

    /**
     * Draft – USER
     */
    const buildUserDraftFormData = (levels) => {
        const formData = new FormData();
        appendCommonDraftFields(formData);
        formData.append('userId', userId);

        levels.forEach((level, index) => {
            formData.append(`users[${index}].documentationApprovalLevelId`, level.id);

            if (level.type === FIELD_TYPE.TEXT.name) {
                formData.append(`users[${index}].document`, level.userLevel?.document || "");
            }
            else {
                const file = level.userLevel?.fileUpload;
                if (file) {
                    formData.append(`users[${index}].fileUploadDto.files`, file);
                    formData.append(`users[${index}].fileUploadDto.userFileNames`, file.name);
                }
            }
        });

        return formData;
    };

    /**
     * Save Draft
     */
    const handleSaveDraft = () => {
        const levels = item.levels.filter(level => level.userLevel?.modified === true);

        if (levels.length === 0) {
            toast.warn(t('not_even_upload_any_input'));
            return;
        }

        const formData =
            profile === PROFILE_MODEL.VEHICLE
                ? buildVehicleDraftFormData(levels)
                : buildUserDraftFormData(levels);

        dispatch(endpoint(formData))
            .then(({ payload: { data: { success, code } } }) => {
                if (success) {
                    setModified(true);
                    toast.success(t('has_been_save_draft_successfully'));
                } else errorMsg(code);
            });
    };

    /**
     * Validation
     */
    const areAllLevelsComplete = () =>
        item.levels.every((level, index) => {
            if (level.type === FIELD_TYPE.TEXT.name) {
                return Boolean(level.userLevel?.document?.trim());
            }
            return (
                filesByLevel?.[index] instanceof File ||
                level.userLevel?.fileUploadDto != null
            );
        });

    /**
     * Submit – VEHICLE
     */
    const buildVehicleSubmitFormData = () => {
        const formData = new FormData();
        appendCommonSubmitFields(formData);
        formData.append('vehicleId', userId);

        item.levels.forEach((level, index) => {
            formData.append(`vehicles[${index}].documentationApprovalLevelId`, level.id);

            if (level.type === FIELD_TYPE.TEXT.name) {
                formData.append(`vehicles[${index}].document`, level.userLevel?.document || '');
            }
            else {
                const file = filesByLevel[index];
                if (file instanceof File) {
                    formData.append(`vehicles[${index}].fileUploadDto.files`, file);
                    formData.append(`vehicles[${index}].fileUploadDto.userFileNames`, file.name);
                }
            }
        });

        return formData;
    };

    /**
     * Submit – USER
     */
    const buildUserSubmitFormData = () => {
        const formData = new FormData();
        appendCommonSubmitFields(formData);
        formData.append('userId', userId);

        item.levels.forEach((level, index) => {
            formData.append(`users[${index}].documentationApprovalLevelId`, level.id);

            if (level.type === FIELD_TYPE.TEXT.name) {
                formData.append(`users[${index}].document`, level.userLevel?.document || '');
            }
            else {
                const file = filesByLevel[index];
                if (file instanceof File) {
                    formData.append(`users[${index}].fileUploadDto.files`, file);
                    formData.append(`users[${index}].fileUploadDto.userFileNames`, file.name);
                }
            }
        });

        return formData;
    };

    /**
     * Submit Save
     */
    const handleSubmitSave = () => {
        if (!areAllLevelsComplete()) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_document_fields'));
            return;
        }

        const formData = profile === PROFILE_MODEL.VEHICLE
                ? buildVehicleSubmitFormData()
                : buildUserSubmitFormData();

        dispatch(endpoint(formData))
            .then(({ payload: { data: { success, code } } }) => {
                if (success) {
                    setModified(true);
                    toast.success(t('has_been_submit_successfully'));
                } else errorMsg(code);
            });
    };

    /**
     * updates a specific user level with the provided changes
     *
     * @param levelIndex index of the level to update
     * @param changes partial changes to apply to userlevel
     */
    const updateUserLevel = (levelIndex, changes) => {
        setItem(prevItem => ({
            ...prevItem,
            levels: prevItem.levels.map((level, index) =>
                index === levelIndex
                    ? {
                        ...level,
                        userLevel: {
                            ...level.userLevel,
                            ...changes,
                        },
                    }
                    : level
            ),
        }));
    };

    /**
     * handles file changes for specific level
     *
     * @param file upload level
     * @param levelIndex index of the level
     */
    const handleChangeFile = (file, levelIndex) => {
        updateUserLevel(levelIndex, { fileUpload: file, modified: true });
    };

    /**
     * handles text input changes a specific level
     *
     * @param e input change evet
     * @param levelIndex index of the level
     */
    const handleChange = (e, levelIndex) => {
        const { name, value } = e.target;
        updateUserLevel(levelIndex, { [name]: value, modified: true });
    };

    return {
        item,
        handleSaveDraft,
        handleSubmitSave,
        getStatusIcon,
        handleChange,
        emptyFields,
        showModal,
        setShowModal,
        photoPicker,
        setPhotoPicker,
        elementRef,
        setActiveLevelIndex,
        levelRefsRef,
        handleSelectLevel,
        highlightedLevel,
        availableHeightColumnOneRef,
        availableHeightColumnTwoRef,
        containerHeightRef
    }
}
